import { IMatrix4, IVector2, CameraMode } from '../../api.js';
import { Camera as BABYLON_Camera } from '@inter-ikea-betrakta-babylonjs/core/Cameras/camera.js';
import { Vector3 as BABYLON_Vector3, Matrix as BABYLON_Matrix } from '@inter-ikea-betrakta-babylonjs/core/Maths/math.vector.js';
import { imatrix4_equals_babylon_matrix, matrix_pool, vector4_pool } from '../../Math.js';
/**
 * This is a wrapper to easily supply custom projection and view matrices, through set_projection_matrix and set_view_matrix respectively.
**/
export class BabylonEngineCamera extends BABYLON_Camera {
    constructor(name, scene, setActiveOnSceneIfNoneActive) {
        super(name, new BABYLON_Vector3(0, 0, 0), scene, setActiveOnSceneIfNoneActive);
        this._cached_view_matrix = BABYLON_Matrix.Identity();
    }
    _getViewMatrix() {
        return this._cached_view_matrix;
    }
    getViewMatrix(force) {
        return this._cached_view_matrix;
    }
    getProjectionMatrix(force) {
        return this._projectionMatrix;
    }
    process_new_view_matrix() {
        // Fixes issue with frustum planes not being updated from the beginning
        // @ts-expect-error
        this._refreshFrustumPlanes = true;
        // Call super function to calculate some internal fields
        super.getViewMatrix(true);
        // We need to manually update the _globalPosition field, otherwise reflections won't work.
        // We use the internal worldMatrix, calculated by getViewMatrix above (inverse of view matrix)
        this._globalPosition.x = this._worldMatrix.m[12];
        this._globalPosition.y = this._worldMatrix.m[13];
        this._globalPosition.z = this._worldMatrix.m[14];
    }
    set_view_matrix(matrix) {
        this._cached_view_matrix.copyFrom(matrix);
        this.process_new_view_matrix();
    }
    process_new_projection_matrix() {
        // Fixes issue with frustum planes not being updated from the beginning
        // @ts-expect-error
        this._refreshFrustumPlanes = true;
        this.freezeProjectionMatrix();
    }
    set_projection_matrix(matrix, projection_type) {
        this._projectionMatrix.copyFrom(matrix);
        if (projection_type === CameraMode.ORTHOGRAPHIC) {
            this.mode = BABYLON_Camera.ORTHOGRAPHIC_CAMERA;
        }
        else if (projection_type === CameraMode.PERSPECTIVE) {
            this.mode = BABYLON_Camera.PERSPECTIVE_CAMERA;
        }
        else {
            console.error('Unknown CameraMode enum: ' + projection_type + '. Expected ORTHOGRAPHIC or PERSPECTIVE');
        }
        this.process_new_projection_matrix();
    }
}
let engine_camera_id_counter = 0;
/**
 * The Engine camera is responsible for passing rendering properties to the rendering pipeline such as view and projection matrices.
 * It holds no understanding of the scene and is only usefull for passing properties to the rendering pipeline.
 */
export class EngineCamera {
    constructor(viewer) {
        this._current_jitter = { x: 0, y: 0 };
        this._view_matrix = IMatrix4.Identity();
        this._camera_mode = CameraMode.PERSPECTIVE;
        // value used to increase (or decrease) how much we jitter, this is useful when debugging to be able to see if our TAA stabilizes.
        this.jitter_multiplier = 1;
        this._is_dirty = true;
        this._scene = viewer.scene;
        this._camera_id = engine_camera_id_counter++;
        this._viewer = viewer;
        // We can input any position since its not used for anything here.
        this._camera = new BabylonEngineCamera(`engine_camera_${this._camera_id}`, this._scene, true);
        this._non_jittered_projection_matrix = BABYLON_Matrix.Identity();
        this._jittered_projection_matrix = BABYLON_Matrix.Identity();
        // Set a default view and projection matrix so you can actually see something on screen if you don't load any camera
        const aspect_ratio = this._scene.getEngine().getRenderWidth() / this._scene.getEngine().getRenderHeight();
        const horizontal_fov = Math.PI / 4;
        const z_near = 0.05;
        const z_far = 100.0;
        const t = 1.0 / Math.tan(horizontal_fov * 0.5);
        const a = t;
        const b = t * aspect_ratio;
        const c = (z_far + z_near) / (z_far - z_near);
        const d = (-2 * z_far * z_near) / (z_far - z_near);
        const projection_matrix = IMatrix4.from_values(a, 0.0, 0.0, 0.0, 0.0, b, 0.0, 0.0, 0.0, 0.0, c, 1.0, 0.0, 0.0, d, 0.0);
        this.set_projection_matrix(projection_matrix, CameraMode.PERSPECTIVE);
        const inverse_sqrt2 = 1.0 / Math.sqrt(2);
        this.set_view_matrix(IMatrix4.from_values(0, -inverse_sqrt2, -inverse_sqrt2, 0, 0, inverse_sqrt2, -inverse_sqrt2, 0, -1, 0, 0, 0, 0, 0, 9.8999, 1));
    }
    get projection_matrix() {
        return this._non_jittered_projection_matrix;
    }
    get view_matrix() {
        return this._camera.getViewMatrix();
    }
    get jittered_projection_matrix() {
        return this._jittered_projection_matrix;
    }
    get_view_matrix() {
        return IMatrix4.copy(this._view_matrix);
    }
    get_projection_matrix() {
        return this._non_jittered_projection_matrix.toIMatrix4ToRef([
            { x: 0, y: 0, z: 0, w: 0 },
            { x: 0, y: 0, z: 0, w: 0 },
            { x: 0, y: 0, z: 0, w: 0 },
            { x: 0, y: 0, z: 0, w: 0 }
        ]);
    }
    get_camera_mode() {
        return this._camera_mode;
    }
    set_view_matrix(matrix) {
        if (IMatrix4.equals(this._view_matrix, matrix))
            return;
        IMatrix4.copy_to_ref(matrix, this._view_matrix);
        const tmp_matrix = matrix_pool.fetch();
        this._camera.set_view_matrix(tmp_matrix.fromIMatrix4(matrix));
        matrix_pool.return(tmp_matrix);
        this._is_dirty = true;
    }
    update_projection_matrix(new_projection_matrix) {
        if (new_projection_matrix) {
            this._non_jittered_projection_matrix.fromIMatrix4(new_projection_matrix);
        }
        this._jittered_projection_matrix.copyFrom(this._non_jittered_projection_matrix);
        if (this._camera_mode === CameraMode.PERSPECTIVE) {
            const z_row = this._jittered_projection_matrix.getRowToRef(2, vector4_pool.fetch());
            this._jittered_projection_matrix.setRowFromFloats(2, this._current_jitter.x * this.jitter_multiplier, this._current_jitter.y * this.jitter_multiplier, z_row.z, z_row.w);
            vector4_pool.return(z_row);
        }
        else if (this._camera_mode === CameraMode.ORTHOGRAPHIC) {
            // Babylon's function getRowToRef silently fails if you supply it row index 3, which is a completely valid index. So we use getRow instead, which does work.
            const w_row = this._jittered_projection_matrix.getRow(3);
            this._jittered_projection_matrix.setRowFromFloats(3, w_row.x + this._current_jitter.x * this.jitter_multiplier, w_row.y + this._current_jitter.y * this.jitter_multiplier, w_row.z, w_row.w);
        }
        else {
            console.error('Missing jitter implementation for camera mode in engine camera!');
        }
        this._camera.set_projection_matrix(this._jittered_projection_matrix, this._camera_mode);
    }
    set_projection_matrix(matrix, projection_type) {
        // We only want to dirty if the given projection changed.
        this._is_dirty = this._is_dirty || !imatrix4_equals_babylon_matrix(matrix, this._non_jittered_projection_matrix) || this._camera_mode !== projection_type;
        this._camera_mode = projection_type;
        this.update_projection_matrix(matrix);
    }
    set_jitter(jitter) {
        if (this._current_jitter.x === jitter.x && this._current_jitter.y === jitter.y)
            return;
        this._current_jitter.x = jitter.x;
        this._current_jitter.y = jitter.y;
        this.update_projection_matrix();
    }
    get_jitter() {
        return IVector2.copy(this._current_jitter);
    }
    // TODO: This is leaking babylon
    get camera() {
        return this._camera;
    }
    is_dirty() {
        return this._is_dirty;
    }
    clear_dirty() {
        this._is_dirty = false;
    }
    dispose() {
        if (this._camera.isDisposed())
            return;
        this._camera.dispose();
    }
    get viewer() {
        return this._viewer;
    }
    get position() {
        return {
            x: this._camera.globalPosition.x,
            y: this._camera.globalPosition.y,
            z: this._camera.globalPosition.z
        };
    }
    // Helper functions to get basis vectors
    get forward() {
        const view_matrix = this._camera.getViewMatrix();
        return {
            x: view_matrix.m[4 * 0 + 2],
            y: view_matrix.m[4 * 1 + 2],
            z: view_matrix.m[4 * 2 + 2]
        };
    }
    get backward() {
        const view_matrix = this._camera.getViewMatrix();
        return {
            x: -view_matrix.m[4 * 0 + 2],
            y: -view_matrix.m[4 * 1 + 2],
            z: -view_matrix.m[4 * 2 + 2]
        };
    }
    get left() {
        const view_matrix = this._camera.getViewMatrix();
        return {
            x: -view_matrix.m[4 * 0 + 0],
            y: -view_matrix.m[4 * 1 + 0],
            z: -view_matrix.m[4 * 2 + 0]
        };
    }
    get right() {
        const view_matrix = this._camera.getViewMatrix();
        return {
            x: view_matrix.m[4 * 0 + 0],
            y: view_matrix.m[4 * 1 + 0],
            z: view_matrix.m[4 * 2 + 0]
        };
    }
    get up() {
        const view_matrix = this._camera.getViewMatrix();
        return {
            x: view_matrix.m[4 * 0 + 1],
            y: view_matrix.m[4 * 1 + 1],
            z: view_matrix.m[4 * 2 + 1]
        };
    }
    get down() {
        const view_matrix = this._camera.getViewMatrix();
        return {
            x: -view_matrix.m[4 * 0 + 1],
            y: -view_matrix.m[4 * 1 + 1],
            z: -view_matrix.m[4 * 2 + 1]
        };
    }
}
//# sourceMappingURL=EngineCamera.js.map